import { createAuthenticatedApiClient } from '../config/api';

// Types for display polling status
interface DisplayPollingStatus {
  state: 'unregistered' | 'registered' | 'active' | 'error';
  clientId?: string;
  registrationData?: any;
  error?: string;
  isPolling: boolean;
  pollingType?: 'registration' | 'activation';
}

// Type for display settings API response
interface DisplaySettingsResponse {
  isRegistered: boolean;
  registration_id?: string;
  name?: string;
  customName?: string;
  status?: string;
  static_auth_token?: string;
  configuration?: any;
}

// Create API client for display operations
const apiClient = createAuthenticatedApiClient();

/**
 * DisplayPollingService
 * 
 * Static class service for managing display registration and activation polling.
 * Follows progressive polling strategy: 5-second intervals initially, 
 * backing off to 30-second intervals after 5 minutes.
 */
export class DisplayPollingService {
  private static pollingInterval: NodeJS.Timeout | null = null;
  private static currentState: 'idle' | 'registration' | 'activation' = 'idle';
  private static listeners: Set<(status: DisplayPollingStatus) => void> = new Set();
  private static clientId: string | null = null;
  private static registrationData: any = null;
  private static pollingStartTime: number | null = null;
  private static currentIntervalMs: number = 5000; // Start with 5-second intervals
  private static retryCount: number = 0;
  private static maxRetries: number = 3;
  private static isRateLimited: boolean = false;
  private static rateLimitBackoffMs: number = 5000; // Start with 5 second backoff
  private static maxRateLimitBackoffMs: number = 300000; // Max 5 minutes backoff

  /**
   * Start polling for display registration status
   */
  static startRegistrationPolling(clientId: string): void {
    console.log(`[DisplayPollingService] Starting registration polling for clientId: ${clientId}`);
    
    // Prevent multiple polling instances
    if (this.currentState !== 'idle') {
      console.log(`[DisplayPollingService] Polling already active (${this.currentState}), ignoring start request`);
      return;
    }
    
    this.clientId = clientId;
    this.currentState = 'registration';
    this.pollingStartTime = Date.now();
    this.retryCount = 0;
    this.rateLimitBackoffMs = 5000; // Reset backoff
    this.isRateLimited = false;
    
    // Clear any existing data
    this.registrationData = null;
    
    // Start polling immediately, then continue with interval
    this.startPollingLoop();
  }

  /**
   * Start activation polling for a registered display
   * Now simplified - just continues registration polling until display is active
   */
  static startActivationPolling(clientId: string, registrationData: any): void {
    console.log('[DisplayPollingService] Starting activation polling for clientId:', clientId);
    
    // Store the registration data and client ID
    this.clientId = clientId;
    this.registrationData = registrationData;
    
    // Just continue registration polling - it will detect when display becomes active
    this.currentState = 'registration';
    this.pollingStartTime = Date.now();
    
    console.log('[DisplayPollingService] Checking activation status');
    this.startPollingLoop();
  }

  /**
   * Stop all polling
   */
  static stopPolling(): void {
    console.log('[DisplayPollingService] Stopping polling');
    
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    
    this.currentState = 'idle';
    this.pollingStartTime = null;
    this.retryCount = 0;
    this.isRateLimited = false;
    this.rateLimitBackoffMs = 5000;
  }

  /**
   * Subscribe to polling status updates
   */
  static subscribe(callback: (status: DisplayPollingStatus) => void): void {
    this.listeners.add(callback);
  }

  /**
   * Unsubscribe from polling status updates
   */
  static unsubscribe(callback: (status: DisplayPollingStatus) => void): void {
    this.listeners.delete(callback);
  }

  /**
   * Handle display deactivation by starting reactivation polling
   * Simplified to use registration polling for reactivation detection
   */
  static handleDisplayDeactivation(displayId?: string, authToken?: string): void {
    console.log('[DisplayPollingService] Handling display deactivation, starting reactivation polling');
    
    // If we have auth token, we can use it to identify the display
    if (authToken && displayId) {
      console.log('[DisplayPollingService] Display deactivated, starting reactivation polling');
      
      // Create minimal registration data for reactivation polling
      this.registrationData = {
        registration_id: displayId,
        static_auth_token: authToken,
        status: 'inactive'
      };
      
      // Use registration polling to detect reactivation
      this.currentState = 'registration';
      this.pollingStartTime = Date.now();
      this.retryCount = 0;
      
      // Start polling for reactivation
      console.log('[DisplayPollingService] Starting reactivation polling...');
      this.startPollingLoop();
      
      // Emit status update
      this.emitStatusUpdate({
        state: 'registered',
        registrationData: this.registrationData,
        isPolling: true,
        pollingType: 'registration'
      });
    } else {
      console.log('[DisplayPollingService] Display deactivated but no auth token available for reactivation polling');
      
      // Clear stored data since we can't poll for reactivation
      this.clearStoredData();
      
      this.emitStatusUpdate({
        state: 'unregistered',
        isPolling: false
      });
    }
  }

  /**
   * Handle display deletion (404 errors)
   */
  static handleDisplayDeletion(): void {
    console.log('[DisplayPollingService] Handling display deletion');
    
    this.stopPolling();
    this.clearStoredData();
    this.emitStatusUpdate({
      state: 'unregistered', // Back to QR code
      isPolling: false
    });
  }

  /**
   * Start the polling loop
   */
  private static startPollingLoop(): void {
    // Always clear any existing interval first
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    
    // If we're rate limited, don't start a new loop
    if (this.isRateLimited) {
      console.log('[DisplayPollingService] Rate limited, not starting new polling loop');
      return;
    }
    
    // Update polling interval based on elapsed time
    this.updatePollingInterval();
    
    console.log(`[DisplayPollingService] Starting polling loop with interval: ${this.currentIntervalMs}ms`);
    
    // Set up the polling interval
    this.pollingInterval = setInterval(() => {
      this.executePollingCheck();
    }, this.currentIntervalMs);
    
    // Execute first check immediately
    this.executePollingCheck();
  }

  /**
   * Execute the appropriate polling check based on current state
   */
  private static async executePollingCheck(): Promise<void> {
    // Don't execute if we're rate limited
    if (this.isRateLimited) {
      console.log('[DisplayPollingService] Skipping polling check due to rate limiting');
      return;
    }
    
    try {
      // Only use registration polling now - it handles both registration and activation detection
      await this.checkRegistrationStatus();
      
      // Reset retry count on successful poll
      this.retryCount = 0;
      
      // Reset rate limit state on successful request
      this.isRateLimited = false;
      
      // Reset rate limit backoff on successful poll
      this.rateLimitBackoffMs = 5000;
      
      // Update polling interval for progressive strategy
      this.updatePollingInterval();
      
    } catch (error: any) {
      console.error('[DisplayPollingService] Polling check failed:', error);
      this.handlePollingError(error);
    }
  }

  /**
   * Check registration status and handle activation
   */
  private static async checkRegistrationStatus(): Promise<void> {
    try {
      const response = await apiClient.get<{success: boolean, data: DisplaySettingsResponse}>('/display/settings', {
        headers: {
          'x-client-id': this.clientId || undefined
        }
      });

      // Extract the nested data from the API response
      const data = response.data.data;
      
      if (data.isRegistered && data.registration_id) {
        console.log('[DisplayPollingService] Display registered successfully');
        
        // Store registration data
        this.registrationData = {
          registration_id: data.registration_id,
          name: data.name,
          customName: data.customName,
          status: data.status,
          static_auth_token: data.static_auth_token // Include token if available
        };
        
        // Check if display is now active (has been manually activated)
        if (data.status === 'active' && data.static_auth_token) {
          console.log('[DisplayPollingService] Display is now active!');
          
          // Display is fully active - stop polling and emit success
          this.stopPolling();
          this.emitStatusUpdate({
            state: 'active',
            clientId: this.clientId || undefined,
            registrationData: this.registrationData,
            isPolling: false
          });
        } else {
          // Display is registered but not yet activated - continue polling registration
          console.log('[DisplayPollingService] Display registered but not yet activated, continuing to poll...');
          
          this.emitStatusUpdate({
            state: 'registered',
            clientId: this.clientId || undefined,
            registrationData: this.registrationData,
            isPolling: true,
            pollingType: 'registration'
          });
        }
      }
      
    } catch (error: any) {
      // Handle specific error cases
      if (error?.response?.status === 202) {
        // Display pending registration - continue polling
        console.log('[DisplayPollingService] Display pending registration, continuing to poll');
        return;
      }
      
      throw error; // Re-throw other errors for general error handling
    }
  }

  /**
   * Emit status update to all subscribers
   */
  private static emitStatusUpdate(status: DisplayPollingStatus): void {
    console.log('[DisplayPollingService] Emitting status update:', status);
    
    this.listeners.forEach(callback => {
      try {
        callback(status);
      } catch (error) {
        console.error('[DisplayPollingService] Error in status update callback:', error);
      }
    });
  }

  /**
   * Handle polling errors with retry logic
   */
  private static handlePollingError(error: any): void {
    // Handle rate limiting with exponential backoff
    if (error?.response?.status === 429) {
      console.warn(`[DisplayPollingService] Rate limited, implementing backoff for ${this.rateLimitBackoffMs}ms`);
      this.handleRateLimit();
      return;
    }
    
    // Handle other errors with retry logic
    this.retryCount++;
    
    if (this.retryCount >= this.maxRetries) {
      console.error('[DisplayPollingService] Max retries exceeded, stopping polling');
      
      this.stopPolling();
      this.emitStatusUpdate({
        state: 'error',
        clientId: this.clientId || undefined,
        error: 'Polling failed after multiple retries',
        isPolling: false
      });
    } else {
      console.warn(`[DisplayPollingService] Polling error, retry ${this.retryCount}/${this.maxRetries} in ${this.currentIntervalMs}ms`);
      
      // Don't restart immediately for non-rate-limit errors
      // Let the existing interval handle the retry
    }
  }

  /**
   * Handle rate limiting with exponential backoff
   */
  private static handleRateLimit(): void {
    // Stop current polling
    if (this.pollingInterval) {
      clearInterval(this.pollingInterval);
      this.pollingInterval = null;
    }
    
    // Set rate limited flag
    this.isRateLimited = true;
    
    console.log(`[DisplayPollingService] Rate limited, backing off for ${this.rateLimitBackoffMs}ms`);
    
    // Schedule restart after backoff period
    setTimeout(() => {
      console.log('[DisplayPollingService] Rate limit backoff complete, resuming polling');
      this.isRateLimited = false;
      
      // Only restart if we're still supposed to be polling
      if (this.currentState !== 'idle') {
        this.startPollingLoop();
      }
      
      // Increase backoff for next time (exponential backoff)
      this.rateLimitBackoffMs = Math.min(this.rateLimitBackoffMs * 2, this.maxRateLimitBackoffMs);
      
    }, this.rateLimitBackoffMs);
  }

  /**
   * Update polling interval based on elapsed time (progressive polling strategy)
   */
  private static updatePollingInterval(): void {
    if (!this.pollingStartTime) return;
    
    const elapsedMs = Date.now() - this.pollingStartTime;
    const fiveMinutesMs = 5 * 60 * 1000; // 5 minutes
    
    const newIntervalMs = elapsedMs > fiveMinutesMs ? 30000 : 5000; // 30s after 5 minutes, 5s initially
    
    // If interval changed, restart the polling loop with new interval
    if (newIntervalMs !== this.currentIntervalMs) {
      console.log(`[DisplayPollingService] Updating polling interval from ${this.currentIntervalMs}ms to ${newIntervalMs}ms`);
      this.currentIntervalMs = newIntervalMs;
      
      // Only restart if we have an active interval and we're not rate limited
      if (this.pollingInterval && !this.isRateLimited) {
        this.startPollingLoop();
      }
    }
  }

  /**
   * Clear stored data
   */
  private static clearStoredData(): void {
    this.clientId = null;
    this.registrationData = null;
    
    // Clear localStorage if needed
    try {
      localStorage.removeItem('displayClientId');
      localStorage.removeItem('displayRegistrationData');
    } catch (error) {
      console.warn('[DisplayPollingService] Failed to clear localStorage:', error);
    }
  }
}

export type { DisplayPollingStatus }; 